import React, { useState } from "react";
import clsx from "clsx";
import { Manager, Popper, Reference } from "react-popper";
import HorizontalCollapse from "./HorizontalCollapse";
import HorizontalItem from "./HorizontalItem";
import Box from "@mui/material/Box";
import IntlMessages from "../../../../utility/IntlMessages";
import { Fonts } from "../../../../../shared/constants/AppEnums";
import ClientOnlyPortal from "./ClientPortal";
import { useLocation } from "react-router-dom";
import { RouterConfigData } from "../../../../../pages/routesConfig";
import ListItem from "@mui/material/ListItem";
import Icon from "@mui/material/Icon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";

interface HorizontalCollapseProps {
  item: RouterConfigData;
  nestedLevel: number;
  dense?: number;
  roles: any[];
}

const HorizontalGroup: React.FC<HorizontalCollapseProps> = (props) => {
  const [opened, setOpened] = useState<boolean>(false);
  const { item, nestedLevel, roles } = props;
  const location = useLocation();

  const handleToggle = (open: boolean) => {
    setOpened(open);
  };

  function isUrlInChildren(parent: RouterConfigData, url: string) {
    if (!parent.children) {
      return false;
    }

    for (let i = 0; i < parent.children.length; i++) {
      if (parent.children[i].children) {
        if (isUrlInChildren(parent.children[i], url)) {
          return true;
        }
      }

      if (
        parent.children[i].url === url ||
        url.includes(parent!.children![i].url!)
      ) {
        return true;
      }
    }

    return false;
  }

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <ListItem
            ref={ref}
            className={clsx(
              "navItem",
              isUrlInChildren(item, location.pathname) && "active"
            )}
            onMouseEnter={() => handleToggle(true)}
            onMouseLeave={() => handleToggle(false)}
          >
            {item.icon && (
              <Icon color="action" className="navLinkIcon">
                {item.icon}
              </Icon>
            )}
            <ListItemText
              primary={<IntlMessages id={item.messageId} />}
              sx={{
                fontWeight: Fonts.MEDIUM,
              }}
            />
            {nestedLevel > 0 && (
              <IconButton
                sx={{
                  ml: 2,
                }}
                disableRipple
              >
                <Icon
                  sx={{
                    fontSize: 18,
                  }}
                  className="arrow-icon"
                >
                  keyboard_arrow_right
                </Icon>
              </IconButton>
            )}
          </ListItem>
        )}
      </Reference>
      <ClientOnlyPortal selector="#root">
        <Popper placement={nestedLevel === 0 ? "bottom-start" : "right"}>
          {({ ref, style, placement }) =>
            opened && (
              <Box
                ref={ref}
                sx={{
                  ...style,
                  boxShadow: "0 0 3px 0 rgba(0, 0, 0, 0.2)",
                  zIndex: 1110 + nestedLevel,
                  "& .popperClose": {
                    pointerEvents: "none",
                  },
                }}
                data-placement={placement}
                className={clsx({
                  popperClose: !opened,
                })}
              >
                <Grow in={opened}>
                  <Paper
                    onMouseEnter={() => handleToggle(true)}
                    onMouseLeave={() => handleToggle(false)}
                  >
                    {item.children && (
                      <List
                        sx={{
                          px: 0,
                        }}
                      >
                        {item.children.map((item) => (
                          <React.Fragment key={item.id}>
                            {item.type === "group" && (!item.permittedRole || roles.indexOf(item.permittedRole) >= 0) && (
                              <HorizontalGroup
                                roles={roles}
                                item={item}
                                nestedLevel={nestedLevel}
                              />
                            )}

                            {item.type === "collapse"  && (!item.permittedRole || roles.indexOf(item.permittedRole) >= 0) && (
                              <HorizontalCollapse
                                  roles={roles}
                                item={item}
                                nestedLevel={nestedLevel}
                              />
                            )}

                            {item.type === "item"  && (!item.permittedRole || roles.indexOf(item.permittedRole) >= 0) && (
                              <HorizontalItem
                                item={item}
                                nestedLevel={nestedLevel}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </List>
                    )}
                  </Paper>
                </Grow>
              </Box>
            )
          }
        </Popper>
      </ClientOnlyPortal>
    </Manager>
  );
};

export default HorizontalGroup;
